import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { ShowToast } from "../../services/toast";
import { LottieLoading } from "../../components/Loading/LottieLoading";
import { CertificationCallProvider, useCertificationCall } from "./simulation/context/CertificationCallContext";
import { CertificationCall } from "./simulation/components/CertificationCall";
import { CertificationIntro } from "./simulation/components/CertificationIntro";
import { useApp } from "../../services/app-context";
import { getLabel, getUrl } from "./utils/labels";
import { MicPermissionModal } from "../../components/MicPermissionModal";
import { checkMicPermission } from "../../services/mic-permission";

function CertificationSimulationContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const assignmentId = searchParams.get("assignmentId");
  const minimumAttempts = parseInt(searchParams.get("minimumAttempts") ?? "1", 10) || 1;
  const restartNonce = searchParams.get("_r");
  const [certificationData, setCertificationData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showMicModal, setShowMicModal] = useState(false);
  const isBeginningRef = useRef(false);
  const { tenantProfile, certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const certificationSingular = getLabel("certification.singular", labels);
  const certificationSingularRef = useRef(certificationSingular);
  certificationSingularRef.current = certificationSingular;
  const certificationProductName = getLabel("certificationProductName", labels);
  const routeNames = tenantProfile?.routeNames ?? {};
  const certificationRouteUrl = getUrl("certificationRoute", routeNames);
  const certificationRouteUrlRef = useRef(certificationRouteUrl);
  certificationRouteUrlRef.current = certificationRouteUrl;
  const { callStatus, setCallStatus, setAssignment: setContextAssignment } = useCertificationCall();

  // Start certification and fetch details. Do NOT include certificationRouteUrl in deps:
  // it changes when tenantProfile loads and would re-run this effect and call startCertification again.
  // restartNonce changes when the user clicks "Begin Next" from the continue prompt, forcing a new attempt.
  useEffect(() => {
    const fetchData = async () => {
      if (!assignmentId) {
        ShowToast({
          message: "No assignment ID provided",
          type: "error",
        });
        navigate(`/${certificationRouteUrlRef.current}`);
        return;
      }

      try {
        setIsLoadingData(true);
        const response = await api.startCertification(assignmentId);
        const data = response?.data;

        if (!data) {
          ShowToast({
            message: `Failed to start ${certificationSingularRef.current.toLowerCase()}`,
            type: "error",
          });
          navigate(`/${certificationRouteUrlRef.current}`);
          return;
        }

        setCertificationData(data);
        setContextAssignment(data);

        setIsLoadingData(false);
        // After loading, show countdown
        setCallStatus("loading");
      } catch (error: any) {
        ShowToast({
          message: error?.response?.data?.message || `Failed to start ${certificationSingularRef.current.toLowerCase()}`,
          type: "error",
        });
        navigate(`/${certificationRouteUrlRef.current}`);
      }
    };

    fetchData();
  }, [assignmentId, restartNonce, navigate, setContextAssignment, setCallStatus]);

  const handleCountdownComplete = () => {
    // After countdown, show intro screen
    setCallStatus("idle");
  };

  const handleBeginCertification = async () => {
    if (isBeginningRef.current) return;
    isBeginningRef.current = true;

    try {
      const micGranted = await checkMicPermission();
      if (!micGranted) {
        setShowMicModal(true);
        return;
      }

      // When user clicks Begin Certification, start the simulation
      setCallStatus("calling");
    } finally {
      isBeginningRef.current = false;
    }
  };

  const handleCertificationDataUpdate = (newData: any) => {
    // Update certification data when moving to next question
    setCertificationData(newData);
    setContextAssignment(newData);
  };

  // Show loading screen while fetching data
  if (isLoadingData) {
    return <LottieLoading />;
  }

  // Show countdown after loading completes
  if (callStatus === "loading") {
    return (
      <CertificationCall 
        onCountdownComplete={handleCountdownComplete}
        certificationData={certificationData}
        showLoading={true}
        assignmentId={assignmentId!}
      />
    );
  }

  // Show intro screen after countdown
  if (callStatus === "idle") {
    return (
      <>
        <CertificationIntro
          assignment={certificationData}
          primerText={certificationData?.primer?.text}
          onBegin={handleBeginCertification}
        />
        <MicPermissionModal
          isOpen={showMicModal}
          onClose={() => setShowMicModal(false)}
          productName={certificationProductName}
          purpose="record your answers"
        />
      </>
    );
  }

  // Show certification question screen after Begin Certification is clicked
  if (callStatus === "calling") {
    return (
      <CertificationCall
        certificationData={certificationData}
        showLoading={false}
        onCertificationDataUpdate={handleCertificationDataUpdate}
        assignmentId={assignmentId!}
        minimumAttempts={minimumAttempts}
      />
    );
  }

  return null;
}

export function CertificationSimulation() {
  return (
    <CertificationCallProvider>
      <CertificationSimulationContent />
    </CertificationCallProvider>
  );
}
